import React, { useEffect, useState } from 'react';
import { Form, Input, InputNumber, Divider, Typography, Select, Button, Space, message, Switch } from 'antd';
import { ReloadOutlined, EditOutlined, ApiOutlined } from '@ant-design/icons';
import api from '../../services/api';

const { TextArea } = Input;
const { Text } = Typography;

/**
 * Parametre şemasına göre dinamik form alanları oluşturan bileşen
 */
const DynamicForm = ({ schema, codeType }) => {
    const [analyticsAccounts, setAnalyticsAccounts] = useState([]);
    const [loadingAnalytics, setLoadingAnalytics] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [googleAdsAccounts, setGoogleAdsAccounts] = useState([]);
    const [loadingGoogleAds, setLoadingGoogleAds] = useState(false);
    const [facebookAdsAccounts, setFacebookAdsAccounts] = useState([]);
    const [loadingFacebookAds, setLoadingFacebookAds] = useState(false);
    const [instagramAccounts, setInstagramAccounts] = useState([]);
    const [loadingInstagram, setLoadingInstagram] = useState(false);
    
    // Manuel giriş modları
    const [manualModes, setManualModes] = useState({
        google_analytics: false,
        google_ads: false,
        facebook_ads: false,
        instagram: false
    });

    // Google Analytics hesaplarını yükle
    const fetchAnalyticsAccounts = async () => {
        setLoadingAnalytics(true);
        try {
            const response = await api.get('/analytics/accounts');
            if (response.data && response.data.accounts) {
                setAnalyticsAccounts(response.data.accounts);
                if (response.data.accounts.length === 0) {
                    message.info('Google Analytics hesabı bulunamadı. Service account\'a Analytics\'ten izin verdiğinizden emin olun.');
                }
            }
        } catch (error) {
            console.error('Analytics hesapları yüklenirken hata:', error);
            message.error('Google Analytics hesapları yüklenemedi');
        } finally {
            setLoadingAnalytics(false);
        }
    };

    // Google Ads hesaplarını yükle
    const fetchGoogleAdsAccounts = async () => {
        setLoadingGoogleAds(true);
        try {
            const response = await api.get('/google-ads/accounts');
            if (response.data && response.data.accounts) {
                setGoogleAdsAccounts(response.data.accounts);
                if (response.data.accounts.length === 0) {
                    message.info('Google Ads hesabı bulunamadı.');
                }
            }
        } catch (error) {
            console.error('Google Ads hesapları yüklenirken hata:', error);
            message.error('Google Ads hesapları yüklenemedi');
        } finally {
            setLoadingGoogleAds(false);
        }
    };

    // Facebook Ads hesaplarını yükle
    const fetchFacebookAdsAccounts = async () => {
        setLoadingFacebookAds(true);
        try {
            const response = await api.get('/facebook-ads/accounts');
            if (response.data && response.data.accounts) {
                setFacebookAdsAccounts(response.data.accounts);
                if (response.data.accounts.length === 0) {
                    message.info('Facebook Ads hesabı bulunamadı.');
                }
            }
        } catch (error) {
            console.error('Facebook Ads hesapları yüklenirken hata:', error);
            message.error('Facebook Ads hesapları yüklenemedi');
        } finally {
            setLoadingFacebookAds(false);
        }
    };

    // Instagram hesaplarını yükle
    const fetchInstagramAccounts = async () => {
        setLoadingInstagram(true);
        try {
            const response = await api.get('/instagram/accounts');
            if (response.data && response.data.accounts) {
                console.log('Instagram hesapları yüklendi:', response.data.accounts.length, 'hesap');
                
                // Duplicate kontrolü için debug
                const accountIds = response.data.accounts.map(acc => acc.instagramAccountId);
                const uniqueIds = [...new Set(accountIds)];
                if (accountIds.length !== uniqueIds.length) {
                    console.warn('⚠️ Instagram hesaplarında duplicate ID\'ler bulundu!', {
                        total: accountIds.length,
                        unique: uniqueIds.length,
                        duplicates: accountIds.filter((id, index) => accountIds.indexOf(id) !== index)
                    });
                }
                
                setInstagramAccounts(response.data.accounts);
                if (response.data.accounts.length === 0) {
                    message.info('Instagram hesabı bulunamadı.');
                }
            }
        } catch (error) {
            console.error('Instagram hesapları yüklenirken hata:', error);
            message.error('Instagram hesapları yüklenemedi');
        } finally {
            setLoadingInstagram(false);
        }
    };

    // Kod tipi seçildiğinde ilgili hesapları yükle
    useEffect(() => {
        console.log('DynamicForm - codeType:', codeType);
        if (codeType && codeType.code === 'google_analytics') {
            console.log('Google Analytics kod tipi seçildi, hesaplar yükleniyor...');
            fetchAnalyticsAccounts();
        } else if (codeType && codeType.code === 'google_ads') {
            console.log('Google Ads kod tipi seçildi, hesaplar yükleniyor...');
            fetchGoogleAdsAccounts();
        } else if (codeType && codeType.code === 'facebook_ads') {
            console.log('Facebook Ads kod tipi seçildi, hesaplar yükleniyor...');
            fetchFacebookAdsAccounts();
        } else if (codeType && codeType.code === 'instagram') {
            console.log('Instagram kod tipi seçildi, hesaplar yükleniyor...');
            fetchInstagramAccounts();
        }
    }, [codeType]);

    if (!schema || !Array.isArray(schema) || schema.length === 0) {
        return (
            <div style={{ marginBottom: 16 }}>
                <Text type="secondary">Bu kod tipi için parametre tanımlanmamış.</Text>
            </div>
        );
    }

    return (
        <>
            <Divider orientation="left">
                {codeType.name} Parametreleri
            </Divider>
            
            {schema.map(param => {
                console.log('DynamicForm - param:', param, 'codeType.code:', codeType.code);
                
                const formItemProps = {
                    label: param.label,
                    name: ['parameters', param.name],
                    tooltip: param.description,
                    rules: param.required 
                        ? [{ required: true, message: `${param.label} alanı zorunludur` }]
                        : undefined
                };

                // Google Analytics için özel dropdown veya manuel giriş
                if (codeType.code === 'google_analytics' && (param.name === 'propertyId' || param.name === 'id')) {
                    console.log('Google Analytics alanı render ediliyor, manuel mod:', manualModes.google_analytics);
                    return (
                        <React.Fragment key={param.name}>
                            {/* Manuel/Otomatik mod toggle */}
                            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Text strong>Giriş Modu:</Text>
                                <Switch
                                    checkedChildren={<><EditOutlined /> Manuel</>}
                                    unCheckedChildren={<><ApiOutlined /> Otomatik</>}
                                    checked={manualModes.google_analytics}
                                    onChange={(checked) => 
                                        setManualModes(prev => ({ ...prev, google_analytics: checked }))
                                    }
                                />
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    {manualModes.google_analytics ? 'Property ID\'yi manuel girin' : 'Listeden seçin'}
                                </Text>
                            </div>
                            <Form.Item {...formItemProps}>
                                {manualModes.google_analytics ? (
                                    // Manuel giriş modu
                                    param.type === 'number' ? (
                                        <InputNumber 
                                            placeholder="Google Analytics Property ID girin (örn: 123456789)"
                                            style={{ width: '100%' }}
                                        />
                                    ) : (
                                        <Input 
                                            placeholder="Google Analytics Property ID girin (örn: 123456789)"
                                        />
                                    )
                                ) : (
                                    // Otomatik dropdown modu
                                    <Select
                                        placeholder="Google Analytics Property seçin"
                                        loading={loadingAnalytics}
                                        disabled={analyticsAccounts.length === 0}
                                        showSearch
                                        allowClear
                                        style={{ width: '100%' }}
                                        filterOption={(input, option) => {
                                            if (!option || !input) return true;
                                            const label = option.label || '';
                                            return label.toLowerCase().includes(input.toLowerCase());
                                        }}
                                        optionLabelProp="label"
                                        onChange={(value) => {
                                            console.log('Selected value:', value);
                                            console.log('Value type:', typeof value);
                                        }}
                                    >
                                        {analyticsAccounts.map(account => (
                                            <Select.OptGroup key={account.accountId} label={account.accountName}>
                                                {account.properties.map(property => {
                                                    const propertyValue = param.type === 'number' 
                                                        ? parseInt(property.propertyId, 10) 
                                                        : property.propertyId;
                                                    
                                                    return (
                                                        <Select.Option 
                                                            key={property.propertyId} 
                                                            value={propertyValue}
                                                            label={`${property.propertyName} (${property.propertyId})`}
                                                        >
                                                            <div>
                                                                <div>{property.propertyName}</div>
                                                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                                                    ID: {property.propertyId}
                                                                </Text>
                                                            </div>
                                                        </Select.Option>
                                                    );
                                                })}
                                            </Select.OptGroup>
                                        ))}
                                    </Select>
                                )}
                            </Form.Item>
                            {!manualModes.google_analytics && (
                                <div style={{ marginTop: -24, marginBottom: 16, textAlign: 'right' }}>
                                    <Button 
                                        size="small"
                                        icon={<ReloadOutlined />} 
                                        onClick={fetchAnalyticsAccounts}
                                        loading={loadingAnalytics}
                                        title="Google Analytics hesaplarını yenile"
                                    >
                                        Yenile
                                    </Button>
                                </div>
                            )}
                        </React.Fragment>
                    );
                }

                // Google Ads için özel dropdown veya manuel giriş (customerId alanı için)
                if (codeType.code === 'google_ads' && param.name === 'customerId') {
                    console.log('Google Ads alanı render ediliyor, manuel mod:', manualModes.google_ads);
                    return (
                        <React.Fragment key={param.name}>
                            {/* Manuel/Otomatik mod toggle */}
                            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Text strong>Giriş Modu:</Text>
                                <Switch
                                    checkedChildren={<><EditOutlined /> Manuel</>}
                                    unCheckedChildren={<><ApiOutlined /> Otomatik</>}
                                    checked={manualModes.google_ads}
                                    onChange={(checked) => 
                                        setManualModes(prev => ({ ...prev, google_ads: checked }))
                                    }
                                />
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    {manualModes.google_ads ? 'Customer ID\'yi manuel girin' : 'Listeden seçin'}
                                </Text>
                            </div>
                            <Form.Item {...formItemProps}>
                                {manualModes.google_ads ? (
                                    // Manuel giriş modu
                                    <Input 
                                        placeholder="Google Ads Customer ID girin (örn: 123-456-7890)"
                                    />
                                ) : (
                                    // Otomatik dropdown modu
                                    <Select
                                        placeholder="Google Ads Hesabı seçin"
                                        loading={loadingGoogleAds}
                                        disabled={googleAdsAccounts.length === 0}
                                        showSearch
                                        allowClear
                                        style={{ width: '100%' }}
                                        filterOption={(input, option) => {
                                            if (!option || !input) return true;
                                            const label = option.label || '';
                                            return label.toLowerCase().includes(input.toLowerCase());
                                        }}
                                        optionLabelProp="label"
                                        onChange={(value) => {
                                            console.log('Selected Google Ads account:', value);
                                            console.log('Value type:', typeof value);
                                        }}
                                    >
                                        {googleAdsAccounts.map(account => (
                                            <Select.Option 
                                                key={account.customerId} 
                                                value={account.customerId}
                                                label={`${account.customerName} (${account.customerId})`}
                                            >
                                                <div>
                                                    <div>{account.customerName}</div>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                                            ID: {account.customerId}
                                                        </Text>
                                                        <Text type={account.statusCode === 2 ? 'success' : 'secondary'} style={{ fontSize: '11px' }}>
                                                            {account.status}
                                                        </Text>
                                                    </div>
                                                </div>
                                            </Select.Option>
                                        ))}
                                    </Select>
                                )}
                            </Form.Item>
                            {!manualModes.google_ads && (
                                <div style={{ marginTop: -24, marginBottom: 16, textAlign: 'right' }}>
                                    <Button 
                                        size="small"
                                        icon={<ReloadOutlined />} 
                                        onClick={fetchGoogleAdsAccounts}
                                        loading={loadingGoogleAds}
                                        title="Google Ads hesaplarını yenile"
                                    >
                                        Yenile
                                    </Button>
                                </div>
                            )}
                        </React.Fragment>
                    );
                }

                // Facebook Ads için özel dropdown veya manuel giriş (adAccountId alanı için)
                if (codeType.code === 'facebook_ads' && param.name === 'adAccountId') {
                    console.log('Facebook Ads alanı render ediliyor, manuel mod:', manualModes.facebook_ads);
                    return (
                        <React.Fragment key={param.name}>
                            {/* Manuel/Otomatik mod toggle */}
                            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Text strong>Giriş Modu:</Text>
                                <Switch
                                    checkedChildren={<><EditOutlined /> Manuel</>}
                                    unCheckedChildren={<><ApiOutlined /> Otomatik</>}
                                    checked={manualModes.facebook_ads}
                                    onChange={(checked) => 
                                        setManualModes(prev => ({ ...prev, facebook_ads: checked }))
                                    }
                                />
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    {manualModes.facebook_ads ? 'Ad Account ID\'yi manuel girin' : 'Listeden seçin'}
                                </Text>
                            </div>
                            <Form.Item {...formItemProps}>
                                {manualModes.facebook_ads ? (
                                    // Manuel giriş modu
                                    <Input 
                                        placeholder="Facebook Ads Account ID girin (örn: act_123456789)"
                                    />
                                ) : (
                                    // Otomatik dropdown modu
                                    <Select
                                        placeholder="Facebook Ads Hesabı seçin"
                                        loading={loadingFacebookAds}
                                        disabled={facebookAdsAccounts.length === 0}
                                        showSearch
                                        allowClear
                                        style={{ width: '100%' }}
                                        filterOption={(input, option) => {
                                            if (!option || !input) return true;
                                            const label = option.label || '';
                                            return label.toLowerCase().includes(input.toLowerCase());
                                        }}
                                        optionLabelProp="label"
                                        onChange={(value) => {
                                            console.log('Selected Facebook Ads account:', value);
                                            console.log('Value type:', typeof value);
                                        }}
                                    >
                                        {facebookAdsAccounts.map(account => (
                                            <Select.Option 
                                                key={account.adAccountId} 
                                                value={account.adAccountId}
                                                label={`${account.accountName} (${account.adAccountId})`}
                                            >
                                                <div>
                                                    <div>{account.accountName}</div>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                                            ID: {account.adAccountId}
                                                        </Text>
                                                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                                            <Text type="secondary" style={{ fontSize: '11px' }}>
                                                                {account.currency}
                                                            </Text>
                                                            <Text type={account.statusCode === 1 ? 'success' : 'secondary'} style={{ fontSize: '11px' }}>
                                                                {account.status}
                                                            </Text>
                                                        </div>
                                                    </div>
                                                    {account.businessName && (
                                                        <Text type="secondary" style={{ fontSize: '11px' }}>
                                                            İşletme: {account.businessName}
                                                        </Text>
                                                    )}
                                                </div>
                                            </Select.Option>
                                        ))}
                                    </Select>
                                )}
                            </Form.Item>
                            {!manualModes.facebook_ads && (
                                <div style={{ marginTop: -24, marginBottom: 16, textAlign: 'right' }}>
                                    <Button 
                                        size="small"
                                        icon={<ReloadOutlined />} 
                                        onClick={fetchFacebookAdsAccounts}
                                        loading={loadingFacebookAds}
                                        title="Facebook Ads hesaplarını yenile"
                                    >
                                        Yenile
                                    </Button>
                                </div>
                            )}
                        </React.Fragment>
                    );
                }

                // Instagram için özel dropdown veya manuel giriş (instagramAccountId alanı için)
                if (codeType.code === 'instagram' && param.name === 'instagramAccountId') {
                    console.log('Instagram alanı render ediliyor, manuel mod:', manualModes.instagram);
                    return (
                        <React.Fragment key={param.name}>
                            {/* Manuel/Otomatik mod toggle */}
                            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Text strong>Giriş Modu:</Text>
                                <Switch
                                    checkedChildren={<><EditOutlined /> Manuel</>}
                                    unCheckedChildren={<><ApiOutlined /> Otomatik</>}
                                    checked={manualModes.instagram}
                                    onChange={(checked) => 
                                        setManualModes(prev => ({ ...prev, instagram: checked }))
                                    }
                                />
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    {manualModes.instagram ? 'Instagram Account ID\'yi manuel girin' : 'Listeden seçin'}
                                </Text>
                            </div>
                            <Form.Item {...formItemProps}>
                                {manualModes.instagram ? (
                                    // Manuel giriş modu
                                    <Input 
                                        placeholder="Instagram Account ID girin (örn: 17841400455970028)"
                                    />
                                ) : (
                                    // Otomatik dropdown modu
                                    <Select
                                        placeholder="Instagram Hesabı seçin"
                                        loading={loadingInstagram}
                                        disabled={instagramAccounts.length === 0}
                                        showSearch
                                        allowClear
                                        style={{ width: '100%' }}
                                        filterOption={(input, option) => {
                                            if (!option || !input) return true;
                                            const label = option.label || '';
                                            return label.toLowerCase().includes(input.toLowerCase());
                                        }}
                                        optionLabelProp="label"
                                        onChange={(value) => {
                                            console.log('Selected Instagram account:', value);
                                            console.log('Value type:', typeof value);
                                        }}
                                    >
                                        {instagramAccounts.map(account => (
                                            <Select.Option 
                                                key={account.instagramAccountId} 
                                                value={account.instagramAccountId}
                                                label={`@${account.username} (${account.instagramAccountId})`}
                                            >
                                                <div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        {account.profilePictureUrl && (
                                                            <img 
                                                                src={account.profilePictureUrl} 
                                                                alt={account.username}
                                                                style={{ width: 20, height: 20, borderRadius: '50%' }}
                                                                onError={(e) => { e.target.style.display = 'none'; }}
                                                            />
                                                        )}
                                                        <div>@{account.username}</div>
                                                    </div>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                                            ID: {account.instagramAccountId}
                                                        </Text>
                                                    </div>
                                                    {account.linkedAdAccount && (
                                                        <Text type="secondary" style={{ fontSize: '11px' }}>
                                                            Bağlı Reklam Hesabı: {account.linkedAdAccount.name}
                                                        </Text>
                                                    )}
                                                </div>
                                            </Select.Option>
                                        ))}
                                    </Select>
                                )}
                            </Form.Item>
                            {!manualModes.instagram && (
                                <div style={{ marginTop: -24, marginBottom: 16, textAlign: 'right' }}>
                                    <Button 
                                        size="small"
                                        icon={<ReloadOutlined />} 
                                        onClick={fetchInstagramAccounts}
                                        loading={loadingInstagram}
                                        title="Instagram hesaplarını yenile"
                                    >
                                        Yenile
                                    </Button>
                                </div>
                            )}
                        </React.Fragment>
                    );
                }

                // Parametre tipine göre uygun input bileşenini döndür
                switch (param.type) {
                    case 'textarea':
                        return (
                            <Form.Item key={param.name} {...formItemProps}>
                                <TextArea 
                                    placeholder={param.placeholder} 
                                    autoSize={{ minRows: 2, maxRows: 6 }}
                                />
                            </Form.Item>
                        );
                    case 'number':
                        return (
                            <Form.Item key={param.name} {...formItemProps}>
                                <InputNumber 
                                    placeholder={param.placeholder}
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        );
                    case 'password':
                        return (
                            <Form.Item key={param.name} {...formItemProps}>
                                <Input.Password 
                                    placeholder={param.placeholder}
                                />
                            </Form.Item>
                        );
                    case 'url':
                        return (
                            <Form.Item key={param.name}
                                {...formItemProps}
                                rules={[
                                    ...(formItemProps.rules || []),
                                    { 
                                        type: 'url', 
                                        message: 'Lütfen geçerli bir URL giriniz'
                                    }
                                ]}
                            >
                                <Input 
                                    placeholder={param.placeholder || 'https://example.com'}
                                />
                            </Form.Item>
                        );
                    case 'email':
                        return (
                            <Form.Item key={param.name}
                                {...formItemProps}
                                rules={[
                                    ...(formItemProps.rules || []),
                                    { 
                                        type: 'email', 
                                        message: 'Lütfen geçerli bir e-posta adresi giriniz'
                                    }
                                ]}
                            >
                                <Input 
                                    placeholder={param.placeholder || 'example@example.com'}
                                />
                            </Form.Item>
                        );
                    case 'text':
                    default:
                        return (
                            <Form.Item key={param.name} {...formItemProps}>
                                <Input 
                                    placeholder={param.placeholder}
                                />
                            </Form.Item>
                        );
                }
            })}
        </>
    );
};

export default DynamicForm; 